import { Flex, Spacer, Text } from '@chakra-ui/react'
import React, { ReactNode } from 'react'
import IconCall from '~/assets/svgs/icon-call.svg?react'
import IconCalls from '~/assets/svgs/icon-calls.svg?react'
import IconPhoneParkHeavy from '~/assets/svgs/icon-phone-park-heavy.svg?react'
import IconPhoneTransferHeavy from '~/assets/svgs/icon-phone-transfer-heavy.svg?react'
import IconPlus from '~/assets/svgs/icon-plus.svg?react'
import { CircleBadge } from '~/components/shared/CircleBadge'
import { CircleBadgeVariant } from '~/components/shared/componentStyles/CircleBadge.style'
import { ExtensionBadgeVariant } from '~/components/shared/componentStyles/ExtensionBadge.style'
import { QuickActionButtonVariants } from '~/components/shared/componentStyles/QuickActionButton.style'
import { EmptyState } from '~/components/shared/EmptyState'
import { ExtensionBadge } from '~/components/shared/ExtensionBadge'
import { PulsingCircleBadge } from '~/components/shared/PulsingCircleBadge'
import { QuickActionButton } from '~/components/shared/QuickActionButton'
import { TextButton } from '~/components/shared/TextButton'
import {
  ContactDetails,
  ContactNumber,
} from '~/pages/Contacts/viewModels/Contacts.viewmodel'
import { Size } from '~/types/ThemeTypes'
import { formatPhoneNumber } from '~/utils/formatter'
import { PresenceState } from '~/utils/xml'

import { toNounCasing } from '@mangovoice/core'
import { ContactSourceType } from '@mangovoice/types'

import { useEmptyListItemStyle } from './componentStyles/EmptyListItem.style'
import { useQuickDialListItemStyle } from './componentStyles/QuickDialListItem.style'

export const enum ListItemDataType {
  ParkingLot = 'parkingLot',
  EmptyParkingLot = 'emptyParkingLot',
  Extension = 'extension',
  EmptyExtension = 'emptyExtension',
  Contact = 'contact',
  SpeedDial = 'speedDial',
  EmptySpeedDial = 'emptySpeedDial',
}

type EmptyParkingItemData = {
  dataType: ListItemDataType.EmptyParkingLot
  id: number
}
type EmptyLineExtensionItemData = {
  dataType: ListItemDataType.EmptyExtension
  id: number
}
type EmptyListItemData = EmptyParkingItemData | EmptyLineExtensionItemData
type ParkingPresenceListItemData = {
  dataType: ListItemDataType.ParkingLot
  id: number
  label: string
  targetNumber: string
  presence?: PresenceState
}
type LineExtensionListItemData = {
  dataType: ListItemDataType.Extension
  id: number
  name: string
  number: string
  presence?: PresenceState
}
export type RecentlyDialedListItemData = {
  dataType: ListItemDataType.Contact
  id: number
  contact: ContactDetails
}
export type SpeedDialListItemData = {
  dataType: ListItemDataType.SpeedDial
  id: number
  label: string
  number: string
}
export type EmptySpeedDialListItemData = {
  dataType: ListItemDataType.EmptySpeedDial
  id: number
}
export type PresenceListItemData =
  | ParkingPresenceListItemData
  | LineExtensionListItemData
export type ListItemData =
  | PresenceListItemData
  | RecentlyDialedListItemData
  | EmptyListItemData
  | SpeedDialListItemData
  | EmptySpeedDialListItemData
const getPresenceBadge = (presence?: PresenceState) => {
  switch (presence) {
    case PresenceState.Available:
      return <CircleBadge variant={CircleBadgeVariant.Green} />
    case PresenceState.Connecting:
      return <PulsingCircleBadge variant={CircleBadgeVariant.Red} />
    case PresenceState.Unavailable:
      return <CircleBadge variant={CircleBadgeVariant.Red} />
    default:
      return <CircleBadge variant={CircleBadgeVariant.Gray} />
  }
}

interface BaseListItemProps {
  presenceBadge?: React.ReactNode
  extensionBadge?: React.ReactNode
  label: string
  subLabel?: string
  buttons: React.ReactNode
}
const BaseListItem = ({
  presenceBadge,
  extensionBadge,
  label,
  subLabel,
  buttons,
}: BaseListItemProps) => {
  const styles = useQuickDialListItemStyle()
  return (
    <Flex sx={styles.container}>
      {presenceBadge}
      <Flex sx={styles.textContainer}>
        <Flex sx={styles.labelContainer}>
          {extensionBadge}
          <Text sx={styles.labelText} noOfLines={1}>
            {label}
          </Text>
        </Flex>
        {subLabel && (
          <Text sx={styles.subLabelText} noOfLines={1}>
            {subLabel}
          </Text>
        )}
      </Flex>
      <Spacer />
      <Flex sx={styles.buttonsContainer} className="action-buttons">
        {buttons}
      </Flex>
    </Flex>
  )
}

type EmptyListItemProps = {
  onSettingsLinkClick: () => void
}
export const EmptyParkingLotListItem = ({
  onSettingsLinkClick,
}: EmptyListItemProps) => {
  const styles = useEmptyListItemStyle()
  return (
    <Flex sx={styles.container}>
      <Text sx={styles.text}>
        To show parking spots here, adjust your web phone settings
      </Text>
      <TextButton onLinkClick={onSettingsLinkClick}>
        Web Phone Settings
      </TextButton>
    </Flex>
  )
}
export const EmptyLineExtensionListItem = ({
  onSettingsLinkClick,
}: EmptyListItemProps) => {
  const styles = useEmptyListItemStyle()
  return (
    <Flex sx={styles.container}>
      <Text sx={styles.text}>
        Add any line extension to see their availability here
      </Text>
      <TextButton onLinkClick={onSettingsLinkClick}>
        Configure Presence
      </TextButton>
    </Flex>
  )
}
export const EmptySpeedDialListItem = ({
  onSettingsLinkClick,
}: EmptyListItemProps) => {
  const styles = useEmptyListItemStyle()
  return (
    <Flex sx={styles.container}>
      <Text sx={styles.text}>
        Store numbers you frequently call with speed dial
      </Text>
      <TextButton onLinkClick={onSettingsLinkClick}>
        Configure Speed Dial
      </TextButton>
    </Flex>
  )
}

export const EmptyRecentlyDialedListItem = () => {
  return (
    <EmptyState
      icon={IconCalls}
      primaryText="No Recently Dialed Numbers"
      secondaryText="After you make calls your recently dialed numbers will appear here."
    />
  )
}

type ListItemProps = {
  hasActiveCall: boolean
  showNewCallButton: boolean
  onTransfer: (parkTarget: string) => void
  onStartCall: (
    callee:
      | string
      | {
          contactNumber: ContactNumber
          contact: ContactDetails
        },
  ) => void
  onNewCall: (
    callee:
      | string
      | {
          contactNumber: ContactNumber
          contact: ContactDetails
        },
  ) => void
}

type ParkingLotListItemProps = ListItemProps & {
  itemData: ParkingPresenceListItemData
}
export const ParkingLotListItem = ({
  itemData,
  hasActiveCall,
  showNewCallButton,
  onTransfer,
  onStartCall,
}: ParkingLotListItemProps) => {
  const buttons: ReactNode[] = []
  const presenceBadge = getPresenceBadge(itemData.presence)
  const label = itemData.label

  const callee = itemData.targetNumber
  const isUnavailable = itemData.presence === PresenceState.Unavailable

  const startCallTooltip = isUnavailable ? 'Pick up Call' : 'Start Call'
  const handleParkClick = () => {
    if (isUnavailable) return
    onTransfer(callee)
  }
  if (hasActiveCall) {
    buttons.push(
      <QuickActionButton
        key={`${itemData.id}-park`}
        variant={QuickActionButtonVariants.Default_300}
        actionIcon={IconPhoneParkHeavy}
        tooltipText="Park Call"
        onClick={handleParkClick}
        disabled={isUnavailable}
      />,
      <QuickActionButton
        key={`${itemData.id}-new-call`}
        variant={QuickActionButtonVariants.Default_100}
        actionIcon={IconPlus}
        tooltipText="Start a new call"
        onClick={() => onStartCall(callee)}
        disabled={!showNewCallButton}
      />,
    )
  } else {
    buttons.push(
      <QuickActionButton
        key={`${itemData.id}-start-call`}
        variant={QuickActionButtonVariants.Default_300}
        actionIcon={IconCall}
        tooltipText={startCallTooltip}
        onClick={() => onStartCall(callee)}
      />,
    )
  }

  return (
    <BaseListItem
      presenceBadge={presenceBadge}
      label={label}
      buttons={buttons}
    />
  )
}

type LineExtensionListItemProps = ListItemProps & {
  itemData: LineExtensionListItemData
}
export const LineExtensionListItem = ({
  itemData,
  hasActiveCall,
  showNewCallButton,
  onTransfer,
  onStartCall,
}: LineExtensionListItemProps) => {
  const buttons: ReactNode[] = []
  const presenceBadge = getPresenceBadge(itemData.presence)
  const extensionBadge = (
    <ExtensionBadge
      size={Size.sm}
      variant={ExtensionBadgeVariant.LightGray}
      extension={itemData.number}
    />
  )
  const label = itemData.name

  const callee = itemData.number

  if (hasActiveCall) {
    buttons.push(
      <QuickActionButton
        key={`${itemData.id}-transfer`}
        variant={QuickActionButtonVariants.Default_300}
        actionIcon={IconPhoneTransferHeavy}
        tooltipText="Transfer Call"
        onClick={() => onTransfer(callee)}
      />,
      <QuickActionButton
        key={`${itemData.id}-new-call`}
        variant={QuickActionButtonVariants.Default_100}
        actionIcon={IconPlus}
        tooltipText="Start a new call"
        onClick={() => onStartCall(callee)}
        disabled={!showNewCallButton}
      />,
    )
  } else {
    buttons.push(
      <QuickActionButton
        key={`${itemData.id}-start-call`}
        variant={QuickActionButtonVariants.Default_300}
        actionIcon={IconCall}
        tooltipText="Start Call"
        onClick={() => onStartCall(callee)}
      />,
    )
  }

  return (
    <BaseListItem
      presenceBadge={presenceBadge}
      extensionBadge={extensionBadge}
      label={label}
      buttons={buttons}
    />
  )
}

type RecentlyDialedListItemProps = ListItemProps & {
  itemData: RecentlyDialedListItemData
}
export const RecentlyDialedListItem = ({
  itemData,
  hasActiveCall,
  showNewCallButton,
  onTransfer,
  onStartCall,
}: RecentlyDialedListItemProps) => {
  const contact = itemData.contact
  const buttons: ReactNode[] = []
  let extensionBadge: ReactNode | undefined
  let label: string
  let subLabel: string | undefined

  if (contact.numbers === undefined) {
    // No numbers for contact, no label/no button
    return <></>
  }

  const callee = { contact: contact, contactNumber: contact.defaultNumber }
  if (contact.source === ContactSourceType.Extensions) {
    // Extension, need ExtensionBadge/no label
    extensionBadge = (
      <ExtensionBadge
        size={Size.sm}
        variant={ExtensionBadgeVariant.LightGray}
        extension={contact.defaultNumber.number}
      />
    )
    label = contact.name
  } else if (contact.id === 0) {
    // Non-contact number from recents
    label = formatPhoneNumber(contact.defaultNumber.number)
  } else {
    // Regular contact
    label = contact.name
    subLabel = `${toNounCasing(
      contact.defaultNumber.label ?? '',
    )}: ${formatPhoneNumber(contact.defaultNumber.number)}`
  }

  if (hasActiveCall) {
    buttons.push(
      <QuickActionButton
        key={`${contact.id}-transfer`}
        variant={QuickActionButtonVariants.Default_300}
        actionIcon={IconPhoneTransferHeavy}
        tooltipText="Transfer Call"
        onClick={() => onTransfer(callee.contactNumber.number)}
      />,
      <QuickActionButton
        key={`${contact.id}-new-call`}
        variant={QuickActionButtonVariants.Default_100}
        actionIcon={IconPlus}
        tooltipText="Start a new call"
        onClick={() => onStartCall(callee)}
        disabled={!showNewCallButton}
      />,
    )
  } else {
    buttons.push(
      <QuickActionButton
        key={`${contact.id}-start-call`}
        variant={QuickActionButtonVariants.Default_300}
        actionIcon={IconCall}
        tooltipText="Start Call"
        onClick={() => onStartCall(callee)}
      />,
    )
  }

  return (
    <BaseListItem
      extensionBadge={extensionBadge}
      label={label}
      subLabel={subLabel}
      buttons={buttons}
    />
  )
}
type SpeedDialListItems = ListItemProps & {
  itemData: SpeedDialListItemData
}
type SpeedDialListItemProps = Omit<SpeedDialListItems, 'onTransfer'>

export const SpeedDialListItem = ({
  itemData,
  hasActiveCall,
  showNewCallButton,
  onStartCall,
}: SpeedDialListItemProps) => {
  const styles = useQuickDialListItemStyle()
  const buttons: ReactNode[] = []

  const label = itemData.label

  const callee = itemData.number

  if (hasActiveCall) {
    buttons.push(
      <QuickActionButton
        key={`${itemData.id}-new-call`}
        variant={QuickActionButtonVariants.Default_100}
        actionIcon={IconPlus}
        tooltipText="Start a new call"
        onClick={() => onStartCall(callee)}
        disabled={!showNewCallButton}
      />,
    )
  } else {
    buttons.push(
      <QuickActionButton
        key={`${itemData.id}-start-call`}
        variant={QuickActionButtonVariants.Default_300}
        actionIcon={IconCall}
        tooltipText="Start Call"
        onClick={() => onStartCall(callee)}
      />,
    )
  }

  return (
    <Flex sx={styles.container}>
      <Flex sx={styles.textContainer}>
        <Flex sx={styles.labelContainer}>
          <Text sx={styles.labelText} noOfLines={1}>
            {label}
          </Text>
        </Flex>
        {callee && (
          <Text
            sx={{ ...styles.subLabelText, color: 'gray.800' }}
            noOfLines={1}
          >
            {callee}
          </Text>
        )}
      </Flex>
      <Spacer />
      <Flex sx={styles.buttonsContainer} className="action-buttons">
        {buttons}
      </Flex>
    </Flex>
  )
}
